/**
 * UsageDashboard — Doc 25 §F3.
 *
 * Componente principal del reporte de consumo. Se reusa entre la vista
 * tenant (`/settings/usage`) y la vista proyecto
 * (`/projects/$id/usage`). La diferencia entre ambas es el filtro
 * `projectId` que se pasa como prop.
 *
 * Estructura:
 *   - PeriodPicker (controlado, 4 presets)
 *   - KPI cards (saldo, costo, calls, tokens)
 *   - Balance card (admin only, con threshold warning)
 *   - Breakdown panel con selector group_by
 *   - Records ledger paginado
 */

import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, Loader2 } from 'lucide-react'

import {
  useUsageBalance,
  useUsageBreakdown,
  useUsageRecords,
  useUsageSummary,
} from '../../hooks/useUsageReport'
import type { UsageGroupBy } from '../../api/usage'
import {
  formatDateOnly,
  formatDateTime,
  formatInt,
  formatUsd,
  isBalanceLow,
  resolvePeriod,
  type PeriodPreset,
} from './usageHelpers'

/**
 * Resuelve la etiqueta a mostrar en la columna `Valor` del desglose.
 *
 * Prioridad:
 *   1. `row.group_label` si el backend lo pobló (user/project).
 *   2. Si `group_by=day` → formatear el ISO date al locale del user.
 *   3. Si `group_by=agent` o `role` → traducir con i18n
 *      (`usage:agents.<key>` / `usage:roles.<key>`); fallback al
 *      key crudo si no hay traducción.
 *   4. Resto (model, etc.) → el key crudo.
 *   5. Si `key === null` (FK eliminada por ON DELETE SET NULL),
 *      mostrar marcador "(eliminado)".
 */
function resolveBreakdownLabel(args: {
  groupBy: UsageGroupBy
  key: string | null
  label: string | null | undefined
  locale: string
  t: ReturnType<typeof useTranslation>['t']
}): string {
  const { groupBy, key, label, locale, t } = args
  if (key === null) {
    return t('usage:breakdown.deleted_resource')
  }
  if (label) return label
  if (groupBy === 'day') return formatDateOnly(key, locale)
  if (groupBy === 'agent') {
    const translated = t(`usage:agents.${key}`, { defaultValue: '' })
    return translated || key
  }
  if (groupBy === 'role') {
    const translated = t(`usage:roles.${key}`, { defaultValue: '' })
    return translated || key
  }
  return key
}

export interface UsageDashboardProps {
  /** Filtra todas las consultas por proyecto. Cuando se omite, agrega
   *  sobre todo el tenant (vista `/settings/usage`). */
  projectId?: string
  /** Si true muestra la sección balance + threshold warning. La pantalla
   *  tenant lo pasa true para admins; la pantalla proyecto siempre false. */
  showBalance?: boolean
}

const PERIOD_PRESETS: readonly PeriodPreset[] = [
  'last_7d',
  'last_30d',
  'last_90d',
  'this_month',
]

const GROUP_BY_OPTIONS: readonly UsageGroupBy[] = [
  'model',
  'agent',
  'role',
  'day',
  'user',
]

export function UsageDashboard({
  projectId,
  showBalance = false,
}: UsageDashboardProps) {
  const { t, i18n } = useTranslation(['usage'])
  const locale = i18n.language
  const [period, setPeriod] = useState<PeriodPreset>('last_30d')
  const [groupBy, setGroupBy] = useState<UsageGroupBy>('model')

  const { from, to } = useMemo(() => resolvePeriod(period), [period])
  const filters = useMemo(
    () => (projectId ? { from, to, projectId } : { from, to }),
    [from, to, projectId],
  )

  const summary = useUsageSummary(filters)
  const breakdown = useUsageBreakdown(groupBy, filters)
  const records = useUsageRecords({ ...filters, limit: 25 })
  const balance = useUsageBalance({ enabled: showBalance })

  return (
    <div className="space-y-6">
      {/* Period picker */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-medium text-ink-subtle">
          {t('usage:period.label')}:
        </span>
        {PERIOD_PRESETS.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setPeriod(p)}
            className={
              p === period
                ? 'rounded-md border border-brand bg-brand text-canvas px-3 py-1 text-xs font-medium'
                : 'rounded-md border border-line bg-surface hover:bg-surface-muted px-3 py-1 text-xs text-ink-muted'
            }
          >
            {t(`usage:period.${p}`)}
          </button>
        ))}
      </div>

      {/* KPI cards */}
      <KpiCardsGrid
        loading={summary.isLoading}
        error={summary.error}
        balance_usd={summary.data?.balance_usd ?? null}
        threshold_usd={summary.data?.low_balance_threshold_usd ?? null}
        cost_billed_usd={summary.data?.cost.billed_usd ?? '0'}
        calls={summary.data?.calls ?? 0}
        tokens_total={summary.data?.tokens.total ?? 0}
        byok_calls={summary.data?.byok_share.calls ?? 0}
        locale={locale}
        t={t}
      />

      {/* Balance detail (admin only) */}
      {showBalance && balance.data ? (
        <BalanceCard data={balance.data} locale={locale} t={t} />
      ) : null}

      {/* Breakdown */}
      <section className="rounded-lg border border-line bg-surface">
        <header className="flex items-center justify-between px-4 py-3 border-b border-line">
          <h2 className="font-display text-base font-semibold text-ink">
            {t('usage:breakdown.title')}
          </h2>
          <label className="flex items-center gap-2 text-xs text-ink-muted">
            <span>{t('usage:breakdown.group_by_label')}:</span>
            <select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value as UsageGroupBy)}
              className="rounded-md border border-line bg-surface px-2 py-1 text-xs"
            >
              {GROUP_BY_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {t(`usage:breakdown.group_by.${opt}`)}
                </option>
              ))}
            </select>
          </label>
        </header>
        <BreakdownTable
          loading={breakdown.isLoading}
          rows={breakdown.data?.rows ?? []}
          groupBy={groupBy}
          locale={locale}
          t={t}
        />
      </section>

      {/* Records ledger */}
      <section className="rounded-lg border border-line bg-surface">
        <header className="px-4 py-3 border-b border-line">
          <h2 className="font-display text-base font-semibold text-ink">
            {t('usage:records.title')}
          </h2>
        </header>
        <RecordsTable
          pages={records.data?.pages ?? []}
          loading={records.isLoading}
          isFetchingNextPage={records.isFetchingNextPage}
          hasNextPage={records.hasNextPage ?? false}
          onLoadMore={() => records.fetchNextPage()}
          locale={locale}
          t={t}
        />
      </section>
    </div>
  )
}

// ─── KPI Cards ───────────────────────────────────────────────────────────────
function KpiCardsGrid({
  loading,
  error,
  balance_usd,
  threshold_usd,
  cost_billed_usd,
  calls,
  tokens_total,
  byok_calls,
  locale,
  t,
}: {
  loading: boolean
  error: Error | null
  balance_usd: string | null
  threshold_usd: string | null
  cost_billed_usd: string
  calls: number
  tokens_total: number
  byok_calls: number
  locale: string
  t: ReturnType<typeof useTranslation>['t']
}) {
  if (error) {
    return (
      <div className="rounded-lg border border-critical/40 bg-critical/5 px-4 py-3 text-sm text-critical">
        {t('usage:loading_error')}
      </div>
    )
  }
  const balanceLow = isBalanceLow(balance_usd, threshold_usd)
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
      {balance_usd !== null ? (
        <KpiCard
          label={t('usage:kpi.balance')}
          value={formatUsd(balance_usd, locale)}
          loading={loading}
          accent={balanceLow ? 'critical' : 'official'}
          hint={
            balanceLow ? t('usage:kpi.balance_low') : undefined
          }
        />
      ) : null}
      <KpiCard
        label={t('usage:kpi.cost_billed')}
        value={formatUsd(cost_billed_usd, locale)}
        loading={loading}
      />
      <KpiCard
        label={t('usage:kpi.calls')}
        value={formatInt(calls, locale)}
        loading={loading}
      />
      <KpiCard
        label={t('usage:kpi.tokens_total')}
        value={formatInt(tokens_total, locale)}
        loading={loading}
        hint={
          byok_calls > 0
            ? t('usage:kpi.byok_calls') + ': ' + formatInt(byok_calls, locale)
            : undefined
        }
      />
    </div>
  )
}

function KpiCard({
  label,
  value,
  loading,
  accent,
  hint,
}: {
  label: string
  value: string
  loading: boolean
  accent?: 'critical' | 'official'
  hint?: string | undefined
}) {
  const borderClass =
    accent === 'critical'
      ? 'border-critical/50'
      : accent === 'official'
        ? 'border-official/30'
        : 'border-line'
  return (
    <div
      className={`rounded-lg border ${borderClass} bg-surface px-4 py-3`}
    >
      <div className="text-[10px] uppercase tracking-wider font-medium text-ink-subtle">
        {label}
      </div>
      <div className="mt-1 font-display text-2xl font-semibold text-ink">
        {loading ? (
          <Loader2 className="w-5 h-5 animate-spin text-ink-subtle" />
        ) : (
          value
        )}
      </div>
      {hint ? (
        <div className="mt-1 text-[10px] text-ink-subtle">{hint}</div>
      ) : null}
    </div>
  )
}

// ─── Balance card ────────────────────────────────────────────────────────────
function BalanceCard({
  data,
  locale,
  t,
}: {
  data: import('../../api/usage').UsageBalance
  locale: string
  t: ReturnType<typeof useTranslation>['t']
}) {
  const low = isBalanceLow(data.balance_usd, data.low_balance_threshold_usd)
  return (
    <section className="rounded-lg border border-line bg-surface px-4 py-3">
      <header className="flex items-center justify-between mb-2">
        <h2 className="font-display text-base font-semibold text-ink">
          {t('usage:balance.title')}
        </h2>
        {low ? (
          <AlertTriangle className="w-4 h-4 text-critical" />
        ) : null}
      </header>
      <div className="space-y-1 text-xs text-ink-muted">
        {data.low_balance_threshold_usd ? (
          <div>
            {t('usage:balance.threshold_warning', {
              amount: formatUsd(data.low_balance_threshold_usd, locale),
            })}
          </div>
        ) : null}
        <div>
          {t('usage:balance.total_consumed', {
            amount: formatUsd(data.total_consumed_usd, locale),
          })}
        </div>
        <div>
          {t('usage:balance.last_topup', {
            when: formatDateTime(data.last_topup_at, locale),
          })}
        </div>
        <div>
          {t('usage:balance.last_consumption', {
            when: formatDateTime(data.last_consumption_at, locale),
          })}
        </div>
        <div className="pt-1">
          {data.block_when_zero
            ? t('usage:balance.block_when_zero_on')
            : t('usage:balance.block_when_zero_off')}
        </div>
      </div>
    </section>
  )
}

// ─── Breakdown table ─────────────────────────────────────────────────────────
function BreakdownTable({
  loading,
  rows,
  groupBy,
  locale,
  t,
}: {
  loading: boolean
  rows: import('../../api/usage').UsageBreakdownRow[]
  groupBy: UsageGroupBy
  locale: string
  t: ReturnType<typeof useTranslation>['t']
}) {
  if (loading) {
    return (
      <div className="px-4 py-6 flex items-center gap-2 text-xs text-ink-muted">
        <Loader2 className="w-4 h-4 animate-spin" />
        {t('usage:records.loading')}
      </div>
    )
  }
  if (rows.length === 0) {
    return (
      <div className="px-4 py-6 text-xs text-ink-muted text-center">
        {t('usage:breakdown.empty')}
      </div>
    )
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="bg-surface-muted text-ink-subtle">
          <tr>
            <th className="text-left px-4 py-2 font-medium">
              {t('usage:breakdown.col.group_key')}
            </th>
            <th className="text-right px-4 py-2 font-medium">
              {t('usage:breakdown.col.calls')}
            </th>
            <th className="text-right px-4 py-2 font-medium">
              {t('usage:breakdown.col.input_tokens')}
            </th>
            <th className="text-right px-4 py-2 font-medium">
              {t('usage:breakdown.col.output_tokens')}
            </th>
            <th className="text-right px-4 py-2 font-medium">
              {t('usage:breakdown.col.cost_billed')}
            </th>
            <th className="text-right px-4 py-2 font-medium">
              {t('usage:breakdown.col.byok')}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr
              key={`${row.group_key ?? 'null'}-${idx}`}
              className="border-t border-line"
            >
              <td className="px-4 py-2 text-[11px]">
                {resolveBreakdownLabel({
                  groupBy,
                  key: row.group_key,
                  label: row.group_label,
                  locale,
                  t,
                })}
              </td>
              <td className="text-right px-4 py-2 tabular-nums">
                {formatInt(row.calls, locale)}
              </td>
              <td className="text-right px-4 py-2 tabular-nums">
                {formatInt(row.input_tokens, locale)}
              </td>
              <td className="text-right px-4 py-2 tabular-nums">
                {formatInt(row.output_tokens, locale)}
              </td>
              <td className="text-right px-4 py-2 tabular-nums font-medium">
                {formatUsd(row.cost_billed_usd, locale)}
              </td>
              <td className="text-right px-4 py-2 tabular-nums text-ink-subtle">
                {row.byok_calls > 0 ? formatInt(row.byok_calls, locale) : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ─── Records table ──────────────────────────────────────────────────────────
function RecordsTable({
  pages,
  loading,
  isFetchingNextPage,
  hasNextPage,
  onLoadMore,
  locale,
  t,
}: {
  pages: import('../../api/usage').UsageRecordsPage[]
  loading: boolean
  isFetchingNextPage: boolean
  hasNextPage: boolean
  onLoadMore: () => void
  locale: string
  t: ReturnType<typeof useTranslation>['t']
}) {
  if (loading && pages.length === 0) {
    return (
      <div className="px-4 py-6 flex items-center gap-2 text-xs text-ink-muted">
        <Loader2 className="w-4 h-4 animate-spin" />
        {t('usage:records.loading')}
      </div>
    )
  }
  const items = pages.flatMap((p) => p.items)
  if (items.length === 0) {
    return (
      <div className="px-4 py-6 text-xs text-ink-muted text-center">
        {t('usage:records.empty')}
      </div>
    )
  }
  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-surface-muted text-ink-subtle">
            <tr>
              <th className="text-left px-4 py-2 font-medium">
                {t('usage:records.col.created_at')}
              </th>
              <th className="text-left px-4 py-2 font-medium">
                {t('usage:records.col.agent')}
              </th>
              <th className="text-left px-4 py-2 font-medium">
                {t('usage:records.col.model')}
              </th>
              <th className="text-right px-4 py-2 font-medium">
                {t('usage:records.col.tokens')}
              </th>
              <th className="text-right px-4 py-2 font-medium">
                {t('usage:records.col.cost')}
              </th>
              <th className="text-center px-4 py-2 font-medium">
                {t('usage:records.col.byok')}
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id} className="border-t border-line">
                <td className="px-4 py-2 font-mono text-[10px] text-ink-muted">
                  {formatDateTime(r.created_at, locale)}
                </td>
                <td className="px-4 py-2 font-mono text-[10px]">
                  {r.agent_name}
                </td>
                <td className="px-4 py-2 font-mono text-[10px]">
                  {r.model_id}
                </td>
                <td className="text-right px-4 py-2 tabular-nums">
                  {formatInt(r.input_tokens + r.output_tokens, locale)}
                </td>
                <td className="text-right px-4 py-2 tabular-nums">
                  {r.byok ? '—' : formatUsd(r.cost_billed_usd, locale)}
                </td>
                <td className="text-center px-4 py-2">
                  {r.byok ? (
                    <span className="inline-block rounded bg-surface-muted px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wider text-ink-subtle">
                      {t('usage:records.byok_chip')}
                    </span>
                  ) : (
                    '—'
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {hasNextPage ? (
        <div className="border-t border-line px-4 py-3 text-center">
          <button
            type="button"
            onClick={onLoadMore}
            disabled={isFetchingNextPage}
            className="text-xs text-brand hover:text-brand-deep disabled:opacity-50"
          >
            {isFetchingNextPage
              ? t('usage:records.loading')
              : t('usage:records.load_more')}
          </button>
        </div>
      ) : null}
    </>
  )
}
